import type { Timestamp } from "firebase/firestore";
import type { SubmissionStatus } from "@coaching/types";

export function clock(t: number): string {
    const s = Math.max(0, Math.floor(t));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export function clockPrecise(t: number): string {
    const hundredths = Math.floor((Math.max(0, t) % 1) * 100);
    return `${clock(t)}.${String(hundredths).padStart(2, "0")}`;
}

export function minutes(sec: number): string {
    const m = Math.round(sec / 60);
    return m === 1 ? "1 minute" : `${m} minutes`;
}

/** "3 clips, 12 minutes" for a bucket. */
export function bucketLength(clipCount: number, totalSec: number): string {
    return `${clipCount} ${clipCount === 1 ? "clip" : "clips"}, ${minutes(totalSec)}`;
}

/** "£10.83" for an amount in pence. */
export function pounds(pence: number): string {
    return `£${(pence / 100).toFixed(2)}`;
}

/** "November 2026" for "2026-11". */
export function monthName(month: string): string {
    const [y, m] = month.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, 15)).toLocaleString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
}

export const STATUS_LABEL: Record<SubmissionStatus, string> = {
    submitted: "Waiting for you",
    accepted: "Accepted",
    in_review: "In progress",
    returned: "Returned",
    declined: "Declined",
    expired: "Expired",
    cancelled: "Cancelled",
};

export function relative(ts: Timestamp | null | undefined, now = Date.now()): string {
    return ts ? relativeMs(ts.toMillis(), now) : "";
}

/** "in 3 h", "2 d ago". */
export function relativeMs(ms: number, now = Date.now()): string {
    const diff = ms - now;
    const abs = Math.abs(diff);
    const unit = abs < 3_600_000 ? [Math.round(abs / 60_000), "min"] as const
        : abs < 86_400_000 ? [Math.round(abs / 3_600_000), "h"] as const
            : [Math.round(abs / 86_400_000), "d"] as const;
    const text = `${unit[0]} ${unit[1]}`;
    return diff < 0 ? `${text} ago` : `in ${text}`;
}

export function dateTime(ts: Timestamp | null | undefined): string {
    if (!ts) return "";
    return ts.toDate().toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
